<template>
  <div class="canvasContainer w-full h-full overflow-scroll-" :class="{ 'map-active': showGoogleMaps && geoReferenceConfigured }" ref="canvasContainer">
    <!-- Capa de Google Maps (FONDO - debajo del canvas) -->
    <div
      v-show="showGoogleMaps && geoReferenceConfigured"
      ref="googleMapsLayer"
      class="google-maps-layer"
      :style="{ opacity: mapOpacity }"
    >
      <!-- Mensaje si Google Maps no está disponible -->
      <div v-if="!isGoogleMapsAvailable" class="google-maps-unavailable">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ $t('google_maps_api_not_loaded') }}</p>
        <small>{{ $t('google_maps_api_add_script') }}</small>
      </div>
    </div>

    <!-- Canvas PixiJS (ENCIMA - con fondo transparente cuando el mapa está activo) -->
    <div id="viewer-pixi" ref="refCanvasPixi" :class="{ 'transparent-mode': showGoogleMaps && geoReferenceConfigured }" style="width: 100%; height: 100%;"> </div>

    <!-- Control de opacidad del MAPA -->
    <div v-if="showGoogleMaps && geoReferenceConfigured" class="map-opacity-control">
      <label>
        <i class="fas fa-map"></i>
        {{ $t('map_opacity') }}: {{ Math.round(mapOpacity * 100) }}%
      </label>
      <input
        type="range"
        min="0.2"
        max="1"
        step="0.1"
        v-model.number="mapOpacity"
      />
    </div>

    <!-- Indicador de culling para debug -->
    <div v-if="showCullingStats" class="culling-stats">
      {{ cullingStats.visible }} / {{ cullingStats.total }} ({{ cullingStats.culled }} {{ $t('hidden_plural') }})
    </div>

    <!-- Indicador de elementos seleccionados -->
    <div v-if="selectedObjects.length > 0" class="selected-stats">
      <span class="selected-icon">✓</span> {{ selectedObjects.length }} {{ $t('selected_plural') }}
    </div>

    <KonvaViewerTooltip ref="ref_tooltip"
      v-show="showTooltip"
      :position="popupPosition"
      :dataTooltip="popupData"
      :statuses="filterStatuses ? filterStatuses : [] "
      :onClose="(e) => { popupData.type = null }"
    />

    <KonvaViewerPopover ref="ref_popover"
      v-show="showPopover"
      :position="popoverPosition"
      :dataTooltip="popoverData"
      :statuses="filterStatuses ? filterStatuses : []"
      @epc-close="handleClosePopover"
      @epc-update-list="handleModifiedEntity"
    />
  </div>
</template>

<script>
import axios from "axios"
import { DataPictogram, PixiViewer } from './utils/index'
import { GeoTransform } from './utils/GeoTransform'
// Loader oficial del proyecto para Google Maps. Carga el SDK una sola vez
// usando el bootstrap importLibrary y resuelve cuando window.google.maps está
// disponible. Configura process.env.MIX_GMAPS_API_KEY en el .env.
import { importGoogleMapsLibraries } from "~/plugins/gmaps-loader"

import KonvaViewerTooltip from "../components/KonvaViewerTooltip.vue";
import KonvaViewerPopover from "../components/KonvaViewerPopover.vue";

import Uppy from '@uppy/core'
// import AwsS3Multipart from '@uppy/aws-s3-multipart'
import AwsS3 from '@uppy/aws-s3';
import store from "~/store";

import { processCompleteAction } from './services/viewer-entities.service';

export default {
    name: "KonvaViewer",

    components: {
      KonvaViewerTooltip,
      KonvaViewerPopover
    },

    props: {
        pictogramId: {
          type: Number,
          required: true,
        },

        pictogramType: {
          type: String,
          required: true,
        },

        pictogramLayers: {
          type: Array,
          required: true,
        },

        pictogramDesign: {
          type: Object,
          required: true,
        },

        workingPictogramLayer: {
          type: Object,
          required: true,
        },

        cadLayersDesign: {
          type: [Object, Array],
          required: true,
        },

        preview: { type: Boolean, default: false },
        scene: { type: Object, default: null },

        workLayer: { type: String, default: null },

        options: {
            default() {
                return {
                    clearColor: "#fff",
                    autoResize: true,
                    colorCorrection: true,
                    sceneOptions: {
                        wireframeMesh: true
                    }
                }
            }
        },

      typeRegister: { type: String, default: 'progress' }, // bandera para indicar que tipo de registro se hace
      canActionsPictogram: { type:Object, default() { return {} } }, // acciones que se puede realizar sobre el pictograma por defecto ninguna
      showLayerCompleted: { type: Boolean, default: false } // visualiza o oculta la capa de estructuras completadas
    },

    data() {
        return {
            isLoading: false,
            progress: null,
            progressText: null,
            curProgressPhase: null,
            error: null,

            dataPictogram: null,

            pics: [], // contiene todas las entidades graficables
            filterPics: [], // solo contiene las entidades graficables filtradas segun tipo 'structure', 'piling', 'module'

            arrTexts: [], // lista de textos a visualizar en la capa respectiva
            arrGroups: [], // para simular una agrupación
            arrStrucComplete: [], // lista de estructuras con estado completado para mostrar como fondo cuando se visualizan los modulos

            popupPosition: { x: -999, y: 0 },
            popupData: { type: null, name: null, status: null, progress: 0 },
            popupXDistanceMin: 0,
            popupYDistanceMin: 0,

            popoverPosition: { x: -9999, y: 0 },
            popoverData: { type: null, name: null, status: null, progress: 0 },
            popoverXDistanceMin: 0,
            popoverYDistanceMin: 0,

            filterStatuses: [], // estados filtrados segun se este visualizando estructuras hincados o modulos
            modifiedEntities: [], // lista de objetos que se modificaron y se mandaran para actualizar el general
            selectedObjects: [], // contiene los objetos seleccionados sobre los cuales se han de realizar acciones o acción
            filterIncidentStatuses: [], // estados de incidentes filtrados segun se este visualizando estructuras hincados o modulos
            toggleDrawing: false,
            regions: [],
            isDrawing: false,
            idRegion: 0,

            startCoords: null,
            endCoords: null,
            rectSelect: null,
            isDraggable: true,

            // fechas en las que se ha realizado el avance
            start_date: null,
            end_date: null,
            type_status: 'progress', // tipo de estado que se va a registrar, puede ser progress o incident

            isViewingOperationalStatuses: false, // para identificar si el usuario esta en en modo de visualizacion de incidencia o progreso
            allowedProgressStatuses: [], // estados de progreso permitidos para el usuario
            allowedIncidentStatuses: [], // estados de incidentes permitidos para el usuario

            // Estadísticas de culling para debug
            showCullingStats: false, // Cambiar a true para debug
            cullingStats: { visible: 0, total: 0, culled: 0 },
            cullingStatsInterval: null,

            // Google Maps integration
            showGoogleMaps: false,
            geoReferenceConfigured: false,
            // Data reactivo que indica si el SDK de Google Maps terminó de cargar.
            // window.google.maps NO es observable por Vue, así que mantenemos un
            // flag local que `_checkGoogleMapsAvailability` actualiza tras resolver
            // la promesa del loader.
            googleMapsLoaded: false,
            geoTransform: null,
            googleMap: null,
            mapOpacity: 0.2, // Opacidad del mapa (0.2 - 1)
        }
    },

    computed: {
      showTooltip: function() {
        return true //this.popupData.type != null
      },

      showPopover: function() {
        return true //this.popoverData.type != null
      },

      isGoogleMapsAvailable() {
        // Reactivo: depende del data `googleMapsLoaded` que actualiza el polling
        // (_checkGoogleMapsAvailability). Antes este computed accedía directo a
        // window.google.maps — como window NO es observable por Vue, el computed
        // no se re-evaluaba y el v-if del template mostraba el mensaje "no cargada"
        // de forma permanente aunque Google Maps ya estuviera disponible.
        return this.googleMapsLoaded;
      },
    },

    watch: {
      showLayerCompleted: function(newValue, oldValue) {
        this.ShowPicComplete(newValue)
      },

      /**
       * Cuando el SDK de Google Maps termina de cargar Y el usuario ya activó
       * showGoogleMaps (porque clickeó el toggle antes de que cargara), arrancar
       * la inicialización del mapa en el siguiente tick.
       */
      googleMapsLoaded(loaded) {
        if (loaded && this.showGoogleMaps && !this.googleMap) {
          this.$nextTick(() => this.initGoogleMaps())
        }
      },
    },

    methods: {
      UpdateCurrentStatusEntities(entities) {
        const entitiesDesign = this.konvaViewer.GetEntitiesDesign(this.workLayer);

        if (!entitiesDesign || entitiesDesign.length === 0) {
          console.warn('No se encontraron entitiesDesign para el layer:', this.workLayer);
          return;
        }

        for (const entity of entities) {
          // El backend retorna 'entity_id', no 'id'
          const entityId = entity.entity_id || entity.id;
          // Buscar por picId (convertir ambos a string para comparación segura)
          const pic = entitiesDesign.find(item => {
            const itemPicId = String(item.picId || '');
            const searchId = String(entityId || '');
            const match = itemPicId === searchId;
            return match;
          });

          if (pic != null) {
            pic.setCurrentStatus(entity.current_status);
          }
        }
      },

      ShowOperationalStatuses(show) {
        this.isViewingOperationalStatuses = show;
        // Usar workLayer si existe, sino obtener todas las entidades del picsContainer
        const entities = this.workLayer
          ? this.konvaViewer.GetEntitiesDesign(this.workLayer)
          : this.konvaViewer.picsContainer.children;

        for (const obj of entities) {
          obj.showOperationalStatus(show);
        }
      },

      getRelativePointerPosition(node) {
        // the function will return pointer position relative to the passed node
        const transform = node.getAbsoluteTransform().copy();
        // to detect relative position we need to invert transform
        transform.invert();

        // get pointer (say mouse or touch) position
        const pos = node.getStage().getPointerPosition();

        // now we find relative point
        return transform.point(pos);
        // return pos;
      },

      _handleMouseDownStage(e) {
        if(this.isDraggable && !this._AllowedAction('select_entity') )
          return

        this.toggleDrawing = true;
        this.isDrawing = true
        const point = this.getRelativePointerPosition(e.target.getStage());

        this.startCoords = point

        this.rectSelect = {
          x: point.x,
          y: point.y,
          width: 0,
          height: 0,
          stroke: 'black',
          fill: "#0c5e95", //"#b3e5ea",
          strokeWidth: 0,
          opacity: 0.6
        }
      },

      _handleMouseMoveStage(e) {
        // if (!this.isDrawing) {
        //   return;
        // }
        // const point = this.getRelativePointerPosition(e.target.getStage());

        // this.endCoords = point
        // let currX = point.x

        // let direction = null
        // if (currX > this.startCoords.x) { // movimiento a la derecha
        //   this.rectSelect.fill = "#0c5e95"
        //   direction = 'right'
        // } else if (currX < this.startCoords.x) { /// movimiento a la izquierda
        //   this.rectSelect.fill = "#5e950c"
        //   direction = 'left'
        // }

        // this.rectSelect.width = this.endCoords.x - this.rectSelect.x
        // this.rectSelect.height = this.endCoords.y - this.rectSelect.y

        // this.$refs.regions.getNode().getLayer().draw()

        // // determinamos con quienes intersecta el area
        // let nodeLayer = this.$refs.layer.getNode().getLayer()
        // let objects = nodeLayer.getChildren().filter((el) => { return !isNaN(el.attrs.id)  })
        // let areaReact = this.rectSelect
        // let objetosDebajo = []

        // // objects.forEach(function(objeto) {
        // // })

        // for (const objeto of objects) {
        //   let indexObjectSelected = this._IndexObjetoSelected(objeto)

        //   // Verificamos si el objeto esta contenido en el rectangulo
        //   if ( this._inArea(direction, objeto, areaReact)) {

        //     // El objeto intersecta con el área, añadirlo al array
        //     if (indexObjectSelected === -1) {
        //       this.selectedObjects.push(objeto);
        //       objeto.fill('#fff7b3');
        //       objeto.opacity(0.6)
        //     }
        //   } else {
        //     // si no esta deseleccionamos
        //     if (indexObjectSelected > -1) {
        //       this.selectedObjects.splice(indexObjectSelected, 1);
        //       objeto.fill( objeto.attrs.status ? objeto.attrs.status.color : null );
        //       objeto.opacity(1)
        //     }
        //   }
        // }
      },

      _inArea(direction, objeto, areaReact) {

        if (direction == 'right') {

          return objeto.attrs.x < areaReact.x + areaReact.width &&
            objeto.attrs.x + objeto.attrs.width > areaReact.x &&
            objeto.attrs.y < areaReact.y + areaReact.height &&
            objeto.attrs.y + objeto.attrs.height > areaReact.y

        } else if (direction == 'left') {

          return objeto.attrs.x < areaReact.x
            && objeto.attrs.x > areaReact.x + areaReact.width
            && objeto.attrs.y > areaReact.y
            && objeto.attrs.y < areaReact.y + areaReact.height
        }
      },

      async Load() {
        this.isLoading = true
        this.$emit('epc-isloading', true)
        this.error = null

        try {
          if ( !this.preview )
            await this.dataPictogram.Load({ url: `/api/v2/pictogram/${this.pictogramId}/json?work_layer=${this.workLayer}`, workLayer: this.workLayer })
          else {
            this.dataPictogram.SetDataPictogram(this.scene)
          }

          this.$emit('epc-loaded');
        } catch (error) {
            console.warn(error)
            this.error = error.toString()
            // Solo ocultar loader si hay error
            this.$emit('epc-isloading', false)
            this.isLoading = false
        }
        // NOTA: No ocultamos el loader aquí - se oculta en mounted() después de _PrepareObjects()
      },


      async Render(type, typePictogram) {
        this.konvaViewer.Render(type, typePictogram)
        this.filterStatuses = await this._FilterStatuses( type )
        this.filterIncidentStatuses = await this._FilterIncidentStatuses( type ) // solo estados de incidentes
      },

      GetLayers() {
        return this.konvaViewer.GetLayers()
      },

      EnableDraggable(enable) {
        this.isDraggable = enable
        this.konvaViewer.EnableDraggable(enable)
      },

      // Metodo para pasar los objetos seleccionas al array modifiedEntities que se guardará, deja en blanco el array de
      // objetos seleccionado
      CompleteAction(data) {
        // Pasamos todos los objetos seleccionados a modifiedEntities
        const result = processCompleteAction(data, this.modifiedEntities, this.typeRegister);

        // TODO: Aqui devemos enblobar para que la fecha sea diferente para cada grupo de entidades modificadas
        this.modifiedEntities = result.updatedEntities;
        this.type_status = result.typeStatus;
        this.start_date = result.startDate;
        this.end_date = result.endDate;
        this.selectedObjects = [];

        // Notificar al padre que la selección se ha limpiado para actualizar el contador
        this.$emit('epc-changestatuses', { selected_entities: this.selectedObjects });
      },

      ShowLayer(name, show) {
        const layer = this.GetLayers().find(  (l) => l.name == name)
        if (!layer) {
            return
        }

        for (const obj of this.konvaViewer.GetEntitiesDesign(this.workLayer)) {
            if ( obj.picLayerName == name)
              obj.visible = show
        }
      },

      ShowIncident(status, show) {
        const entities = this.konvaViewer.GetEntitiesDesign(this.workLayer);
        // Mostrar solo los que coinciden con el status.id
        for (const obj of entities) {
          obj.visible = !show || obj.picIncident === status.id;
        }
      },

      async Save() {
        this.$emit('epc-saving', { loading: true, text: this.$t('generating_images') })

        // Ocultar backgrounds antes de capturar
        if (this.konvaViewer._backgroundsGraphics) {
          this.konvaViewer._backgroundsGraphics.visible = false;
        }

        // 1.- Obtenemos las capturas de las diferentes capas (screenshot en formato png)
        let screenshots = await this.GetScreenshotsLayers()

        // Restaurar fondos después de capturar
        if (this.konvaViewer._backgroundsGraphics) {
          this.konvaViewer._backgroundsGraphics.visible = true;
        }

        // 2.- Guardamos los datos del pictograma
        this.$emit('epc-saving', { loading: true, text: this.$t('saving_pictogram') })
        // primero guardamos las entidades modificadas si todo va bien recien actualizamos datos meta del pictograma y registramos
        // progreso realizado por el usuario
        let response = await this._UpdateEntities()
        if ( response ) {
          // console.log('response: ', response)
          this.$emit('epc-saving', { loading: true, text: this.$t('saving_screenshots') })
          let arrAwsResponse = []
          for (const item of screenshots) {
            const awsr = await this.submitResource(item)
            arrAwsResponse.push(awsr)
          }

          this.$emit('epc-saving', { loading: true, text: this.$t('saving_pictogram_data') })
          response = await this._UpdateDataPictogram(response, arrAwsResponse);
        }

        if (response) this.$emit('epc-reload')

        this.$emit('epc-saving', { loading: false, text: null })
      },

      // Method que obtiene las capturas de las diferentes capas
      async GetScreenshotsLayers() {

        // TODO: para guardar las tres capturas en uno hacer que si worklayers es null or all tomar this.pictogramLayers
        // analizar
        let layers = [{ code: this.workLayer}]
        if(this.pictogramType !== 'mechanical_installation') {
          layers.push({code: 'general'}) // hacks para pictogramas de lineas de evacuacion para general la imagen general
        }
        let screenshots = []
        // console.log('layers: ' , layers)
        for (const lyr of layers) {

          // TODO: esta linea comentada activar cuando se guarde las 3 capturas
          // this._OnClickDisplayLayerPictogram(lyr.code)
          // await this.sleep(5000) // enves de realizar el fit pausamos 1 segundo

          // await this.konvaViewer.FitToScreen()
          // .then(async (result) => {
          //   let img = await this.konvaViewer.TakeScreenshot()
          //   screenshots.push({
          //     name: lyr.code + '.png',
          //     src: img
          //   })
          // })

          await this.konvaViewer.FitToScreen()
          // Se puede activar capa segun lyr.code aqui
          // Esperar un frame para asegurar que pixi renderice
          await new Promise(resolve => requestAnimationFrame(resolve))
          let img = await this.konvaViewer.TakeScreenshot()
          screenshots.push({
            name: lyr.code + '.png',
            src: img
          })

          await this.sleep(500) // Por si las moscas esperamos 1/2 seg entre captura
        }

        // TODO: Activar cuando se guarden las tres capturas (analizar antes)
        // this._OnClickDisplayLayerPictogram(this.displayPictogramLayer)
        // await this.sleep(3000)

        return screenshots
      },

      async _UpdateEntities() {
        let form = new FormData();
        form.append('modified_entities', JSON.stringify(this.modifiedEntities))
        form.append('work_layer', this.workLayer)

        let url = `/api/v2/pictogram/${this.pictogramId}/update-entities`

        // Enviamos el form
        return await axios.post(url, form)
          .then((response) => {
            let data = response.data

            if (data && data.success) {

              console.log('%cEPC-TACKER: '+ '%c entities actualizado correctamente.', 'background: #5577BB; color: #fff', 'color: #000')

              return data.data

            } else if(data && data.msg){

              this.$notify.error('no_access_permissions')

            } else {
              this.$notify.error('error_saving')
            }
          })
          .catch(error => {
            // Manejar errores aquí
            if (error.response) {
              // La solicitud fue hecha y el servidor respondió con un estado de error
              this.$notify.error(error.response.data.message)
              // console.log('Error de respuesta:', error.response.status, error.response.data);
              console.error('%cEPC-TACKER: '+ '%c Error de respuesta:', 'background: #5577BB; color: #fff', 'color: #000', error.response.status, error.response.data)
            } else if (error.request) {
              // La solicitud se realizó pero no se recibió respuesta del servidor
              this.$notify.error('no_server_response')
              console.error('%cEPC-TACKER: '+ '%c No se recibió respuesta del servidor', 'background: #5577BB; color: #fff', 'color: #000')
            } else {
              // Ocurrió un error antes de la solicitud, como una configuración incorrecta de Axios
              this.$notify.error('configuration_error')
              console.error('%cEPC-TACKER: '+ '%c Error de configuración:', 'background: #5577BB; color: #fff', 'color: #000', error.message)
            }

            return null;
          })
          // .finally(() => {
          //   this.$emit('epc-saving', { loading: false, text: null })
          // })
      },

      async _UpdateDataPictogram(partialProgress, awsResponseImages) {
        // 1. contabilizar primero total de entidades por typo y estado
        // TODO: Este paso ya no es necesario, por que esta devolviendo el progreso total anterior al momento de cargar
        // el archivo solo estamos realizando un proceso inecesario directo el calculo lo realizamos en el servidor
        // enviando solo el progreso realizado por el usuario, lo dejamos comentado de momento
        // let totalStatausProgress = this._CalulateProgress(this.filterPics) // total progreso ya es calculado apartir del partian en backend

        // 2. contabilizar total de entidades por tipo y estado modificadas por el usuario
        // let statusesProgress = this._CalulateProgress(this.modifiedEntities) // ahora ya no calculamos apartir de los modificados sino desde el diseño
        let statusesProgress = this._CalulateProgressInDesign()
        let statusesExecuted = this._CalulateExecuted()
        // 3. preparar para mandar al backend
        let form = new FormData();

        form.append('work_layer', this.workLayer)
        // form.append('total_progress', JSON.stringify(totalStatausProgress))
        form.append('partial_progress', JSON.stringify(partialProgress))
        form.append('statuses_progress', JSON.stringify(statusesProgress))
        form.append('statuses_executed', JSON.stringify(statusesExecuted))
        form.append('aws_images', JSON.stringify(awsResponseImages))
        form.append('type_status', this.type_status) // tipo de estado que se va a registrar, puede ser progress o incident

        if (this.start_date) {
          form.append('start_date', this.start_date)
        }

        if (this.end_date) {
          form.append('end_date', this.end_date)
        }

        let url = `/api/v2/pictogram/${this.pictogramId}/update-progress` // add url

        // Enviamos el form
        return await axios.post(url, form)
          .then((response) => {
            let data = response.data

            if (data && data.success) {
              this.$notify.success('success_editing')
              console.log('%cEPC-TACKER: '+ '%c pictograma actualizado correctamente.', 'background: #5577BB; color: #fff', 'color: #000')

              return true;
            } else if(data && data.msg){
              this.$notify.error('no_access_permissions')
            } else {
              this.$notify.error('error_saving')
            }

            return false;
          })
          .catch(error => {
            // Manejar errores aquí
            if (error.response) {
              // La solicitud fue hecha y el servidor respondió con un estado de error
              this.$notify.error(error.response.data.message)
              // console.log('Error de respuesta:', error.response.status, error.response.data);
              console.error('%cEPC-TACKER: '+ '%c Error de respuesta:', 'background: #5577BB; color: #fff', 'color: #000', error.response.status, error.response.data)
            } else if (error.request) {
              // La solicitud se realizó pero no se recibió respuesta del servidor
              this.$notify.error('no_server_response')
              console.error('%cEPC-TACKER: '+ '%c No se recibió respuesta del servidor', 'background: #5577BB; color: #fff', 'color: #000')
            } else {
              // Ocurrió un error antes de la solicitud, como una configuración incorrecta de Axios
              this.$notify.error('configuration_error')
              console.error('%cEPC-TACKER: '+ '%c Error de configuración:', 'background: #5577BB; color: #fff', 'color: #000', error.message)
            }

            return false;
          })
          // .finally(() => {
          //   this.$emit('epc-saving', { loading: false, text: null })
          // })
      },

      async submitResource(resource) {
        const blob = resource.src;
        blob.name = resource.name; // TODO: al nombre del recurso añadir marca de tiempo y name company o project who prefix
        return this.store(blob, {})
        .then(async result => {
          // successFul contiene toda la lista de archivos que se han subido
          // para nuestro caso solo consideramos siempre el primero por que enviamos de a uno
          if( result.successful.length > 0 ) {
            let response = result.successful[0].response

            let params = {
              // uuid: response.uuid,
              key: response.body.key,
              // bucket: response.bucket,
              name: blob.name,
              content_type: blob.type,
              // url: response.url
            }
            return params
            // form.append("aws_response", JSON.stringify(params));
          }

          // failed contiene todos los archivos que tubieron fallo al subir
          if (result.failed.length > 0) {
            console.error('Errors:')
            result.failed.forEach((file) => {
              console.error(file.error)
            })

            return null
          }
        }).catch((error) => {
          console.log('error: ', error)
          return null
        });
      },

      // CUSTOM VAPOR STORE METHOD
      async store(file, options = null) {
        // verificamos si existe algun archivo en la lista de carga de uppy
        // si existe lo eliminamos
        if( this.keyFileUploadCurrent ) {
          this.uppy.removeFile(this.keyFileUploadCurrent);
        }

        if (typeof options.progress === 'undefined') {
            options.progress = () => {};
        }

        this.keyFileUploadCurrent = this.uppy.addFile({
          name: file.name, // file name
          type: file.type, // file type
          data: file, // file blob
          // meta: {
          //   // optional, store the directory path of a file so Uppy can tell identical files in different directories apart.
          //   relativePath: webkitFileSystemEntry.relativePath,
          // },
          source: 'Local',
          isRemote: false,
        });

        // response.data.extension = file.name.split('.').pop()
        return this.uppy.upload()
      },

      _Filter(entities, filter) {
        return entities.filter( (p) => p.type === filter );
      },

      _FilterEntitiesDesign(filter) { //usado porpixi
        return this.konvaViewer.GetEntitiesDesign(filter);
      },

      _FilterStatuses(filter, onlyCompletion=false) {
        if( this.workingPictogramLayer ) {
          return onlyCompletion ? this.workingPictogramLayer.statuses.filter((item) => { return item.is_completion }) : this.workingPictogramLayer.statuses
        }
        return []
      },

      _FilterIncidentStatuses(filter) {
        if( this.workingPictogramLayer ) {
          return this.workingPictogramLayer.incident_statuses
        }
        return []
      },

      _CalulateProgressInDesign() {

        let progress = { }

        if ('mechanical_installation' === this.pictogramType) {
          progress = {
            structures: {
              '-1': 0
            },
            pilings: {
              '-1': 0
            },
            modules: {
              '-1': 0
            }
          }
        } else {
          progress = {
            sections: {
              '-1': 0
            },
          }

          if ('aerial_evacuation_line' === this.pictogramType) {
            progress.nodes = { '-1': 0 }
          }
        }

        let filterStatuses = this.type_status === 'incident'
          ? this._FilterIncidentStatuses(this.workLayer)
          : this._FilterStatuses(this.workLayer)

        let filterEntities = this._FilterEntitiesDesign(this.workLayer)
        // determinamos primero los que no tiene status
        progress[this.workLayer]['-1'] = filterEntities.filter( (o) => o.picStatus == null ).length;

        // determinamos cantidad por status
        let self = this

        filterStatuses.forEach( (status) => {
          let tempEntities = filterEntities.filter( (o) => {
            if (o.picStatus != null) {
              // // si existe un estado anterior verificamos si ese estado no representa completado para evitar
              // // volver a contabilizar el mismo
              // if (o.previou_status != null) {
              //   if (filterStatuses.find(ps => { return ps.id ==  o.previou_status }) != null)
              //     return false
              // }

              let idStatus = typeof o.picStatus === 'object' ? o.picStatus.id : o.picStatus;
              return idStatus == status.id

            } else
              return false
          });

          // if(self.workLayer === 'modules') {
          //   progress[this.workLayer][status.id] = tempEntities.length ? tempEntities.reduce((accumulator, item) => {
          //     let nameLayer = item.picLayerName.replace(/ /g, '_')
          //     let totalPanels = self.pictogram.metadata.cad_layers_design[nameLayer.replace('MODULES_EPC_', '')].total_panels
          //     accumulator += totalPanels;
          //     return accumulator;
          //   }, 0) : 0;
          // } else {
            progress[this.workLayer][status.id] = tempEntities.length;
          // }
        })

        return progress
      },

      _CalulateProgress(entities) {
        let progress = {
          structures: {
            '-1': 0
          },
          pilings: {
            '-1': 0
          },
          modules: {
            '-1': 0
          }
        }

        //TODO: Optimización solo manda los datos en la capa que se ha trabajadado (this.workLayer) no en todas, de momento dejamos comentado el codigo
        // this.pictogramLayers.forEach( (layer, index) => {
        //   let filterStatuses = this._FilterStatuses(layer.code)
        //   let filterEntities = this._Filter(entities, layer.code)

        //   // determinamos primero los que no tiene status
        //   progress[layer.code]['-1'] = filterEntities.filter( (o) => o.status == null ).length;

        //   // determinamos cantidad por status
        //   filterStatuses.forEach( (status) => {
        //     progress[layer.code][status.id] = filterEntities.filter( (o) => o.status != null && o.status.id == status.id ).length;
        //   })
        // })

        let filterStatuses = this._FilterStatuses(this.workLayer)
        let filterEntities = this._Filter(entities, this.workLayer)
        // determinamos primero los que no tiene status
        progress[this.workLayer]['-1'] = filterEntities.filter( (o) => o.status == null ).length;

        // determinamos cantidad por status
        let self = this
        filterStatuses.forEach( (status) => {
          let tempEntities = filterEntities.filter( (o) => {
            if (o.status != null) {
              // // si existe un estado anterior verificamos si ese estado no representa completado para evitar
              // // volver a contabilizar el mismo
              // if (o.previou_status != null) {
              //   if (filterStatuses.find(ps => { return ps.id ==  o.previou_status }) != null)
              //     return false
              // }

              let idStatus = typeof o.status === 'object' ? o.status.id : o.status;
              return idStatus == status.id

            } else
              return false
          });

          if(self.workLayer === 'modules') {
            progress[this.workLayer][status.id] = tempEntities.reduce((acc, item) => {
              let nameLayer = item.layer.replace(/ /g, '_')

              let totalPanels = this.cadLayersDesign[nameLayer.replace('MODULES_EPC_', '')].total_panels
              acc += totalPanels;

              return acc;
            }, {});
          } else {
            progress[this.workLayer][status.id] = tempEntities.length;
          }
        })
        return progress
      },

      _CalulateExecuted() {
        let progress = { }

        let filterStatuses = this.type_status === 'incident'
          ? this._FilterIncidentStatuses(this.workLayer)
          : this._FilterStatuses(this.workLayer);

        filterStatuses.forEach( (status) => {

          if (! progress.hasOwnProperty(this.workLayer)) {
            progress[this.workLayer] = {}
          }

          let filterEntities = this.modifiedEntities.filter( (o) => {
            if (o.status != null) {
              let idStatus = typeof o.status === 'object' ? o.status.id : o.status;
              return idStatus == status.id

            } else
              return false
          });

          if( filterEntities.length > 0 ) {
            progress[this.workLayer][status.id] = filterEntities.reduce((acc, item) => {
              let nameLayer = item.layer.replace(/ /g, '_')

              if (!acc[nameLayer]) {
                acc[nameLayer] =  { nameLayer: item.layer, total: 0 } ;
              }

              // if( item.type === 'modules' ) {
              //   let totalPanels = this.cadLayersDesign[nameLayer.replace('MODULES_EPC_', '')].total_panels
              //   acc[nameLayer].total += totalPanels;
              // } else {
                acc[nameLayer].total += 1;
              // }

              return acc;
            }, {});
          }
        })

        return progress
      },

      async _PrepareObjects() {
        this.konvaViewer.SetScene( this.dataPictogram.GetScene() )

        // Carga asíncrona con reporte de progreso
        let objects = await this.konvaViewer.GetEntities(
          this.pictogramLayers,
          this.typeRegister,
          (current, total) => {
            const percent = Math.round((current / total) * 100);
            this.$emit('epc-loading-progress', { current, total, percent, text: `${this.$t('loading_entities')}: ${percent}%` });
          }
        )

        this.texts = objects.labels
        this.arrTexts = this.texts // optimizar por que se filtrara
        this.arrGroups = objects.backgrounds // optimizar por que se filtrara
        this.arrStrucComplete = objects.completed
        this.pics = objects.entities

        this.$emit('epc-loaded-scene')
      },

      async _PrepareObjectsEvacuationLine() {
        this.konvaViewer.SetScene( this.dataPictogram.GetScene() )

        // Carga asíncrona con reporte de progreso
        let objects = await this.konvaViewer.GetEntitiesEvacuationLine(
          this.pictogramLayers,
          (current, total) => {
            const percent = Math.round((current / total) * 100);
            this.$emit('epc-loading-progress', { current, total, percent, text: `${this.$t('loading_entities')}: ${percent}%` });
          }
        )

        this.texts = objects.labels
        this.arrTexts = this.texts // optimizar por que se filtrara
        this.arrGroups = objects.backgrounds // optimizar por que se filtrara
        this.arrStrucComplete = objects.completed
        this.pics = objects.entities

        this.$emit('epc-loaded-scene')
      },

      _IndexObjetoModified(obj) {
        return this.modifiedEntities.findIndex( (item) => { return item.attrs.id === obj.attrs.id } )
      },

      _IsObjetoSelectedOrModified(obj) {
        return ( _IndexObjetoSelected(obj) !== -1 ) || ( _IndexObjetoModified(obj) !== -1 )
      },

      handleWheel(e) {
        // stop default scrolling
        e.evt.preventDefault();
        this.konvaViewer.ZoomStagePoiter(e, 1.1) //1.01

        this._Screenshot();

        // console.log( 'canvasContainer: ', this.$refs.canvasContainer )
        // html2canvas(this.$refs.canvasContainer, {
        //   scale: 2,
        //   // width: 1280,
        //   // height: 1080
        // }).then(function(canvas) {
        //     console.log( 'canvas: ', canvas )
        //     document.body.appendChild(canvas);
        // });

      },

      handleClosePopover() {
        this.popoverPosition = { x: -9999, y: 0 };
        this.popoverData = { type: null, name: null, status: null, progress: 0 };
      },

      handleModifiedEntity(entity) {
        // 1. Actualizamos datos del array de pictogramas original en este caso solo cambia el estado
        this.filterPics.find( (obj) =>  obj.id == entity.attrs.id).status = entity.status

        // 2. Actualizamos el array de entidades modificadas por el usuario
        let posIndex = this.modifiedEntities.findIndex( (obj) => obj.id == entity.attrs.id )
        if( posIndex === -1) {
          this.modifiedEntities.push( {
            id: entity.attrs.id,
            name: entity.attrs.name,
            type: entity.attrs.type,
            status: entity.status,
          } )
        } else if (posIndex > -1) {
          this.modifiedEntities[posIndex].status = entity.status
        }
      },

      async _Screenshot() {
        return new Promise((resolve, reject) => {
          // let timeOut = null;
          // const l = ()=>{
          //     timeOut && window.clearTimeout(timeOut);
          //     try {
          //       console.log('screenshot')\
              this.$emit('epc-screenshot', this.konvaViewer.Screenshot());
              resolve()
          //       timeOut = window.setTimeout(l, 1e3)
          //     } catch (e) {
          //       console.log(e)
          //     }
          // };
          // l()
        })
      },

      sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
      },

      ZoomIn() {
        this.konvaViewer.ZoomStage(1.1)//1.2
      },

      ZoomOut() {
        this.konvaViewer.ZoomStage(0.9)//-0.8
      },

      Selecting(value, workLayer) {
        if (this.isViewingOperationalStatuses) {
          this.$notify.error('cannot_select_in_incident_mode');
          return;
        }

        this.konvaViewer.Selecting(value, workLayer)
      },

      Unselect() {
        if (this.isViewingOperationalStatuses) {
          this.$notify.error('cannot_select_in_incident_mode');
          return;
        }

        this.konvaViewer.Unselect()

        this.modifiedEntities = []
        this.selectedObjects.forEach( (item) => {
          item.picSelected = false
          item.SelectPic(item)
        })

        this.selectedObjects = []

      },

      Dragging(value) {
        this.konvaViewer.Dragging(value)
      },

      FitToScreen() {

        return new Promise((resolve, reject) => {
          this.konvaViewer.FitToScreen()
            .then(result => {
              // resolve(this._Screenshot());
            })
        })
      },

      // Función para manejar el evento de redimensionamiento
      handleResize() {
        this.konvaViewer.Resize()
      },

      _AllowedAction(action) {
        if (Object.keys(this.canActionsPictogram).length === 0)
          return false;

        return this.canActionsPictogram[action] === 'undefined' ? false : this.canActionsPictogram[action]
      },







      // TODO esta funsion se debe obtimizar lo ideal es que no exista arrStructuresCompleted sino que tolo lo maneje el  PixiViewer
      ShowPicComplete(visible) {
        this.arrStrucComplete.forEach( (obj) => {
          if(obj.picStroke !== '#ffffff')
            obj.visible = visible
        })
      },

      _IndexObjetoSelected(obj) {
        return this.selectedObjects.findIndex( (item) => { return item.picId === obj.picId } )
      },

      _HandleClickPic(e) {

        if( this._AllowedAction('select_entity') ) {

          var receivedObj = e.detail;

          let indexObjectSelected = this._IndexObjetoSelected(receivedObj)

          if (indexObjectSelected > -1) {
            this.selectedObjects.splice(indexObjectSelected, 1);
          } else {
            this.selectedObjects.push(receivedObj);
          }

          let emitData = {
            // show: this.selectedObjects.length > 0,
            selected_entities: this.selectedObjects,
            // statuses: this.filterStatuses,
            // incident_statuses: this.filterIncidentStatuses,
          }

          // estos dos de momento estan aquí luego iran en un menu contextual
          this.$emit('epc-changestatuses', emitData) // habilitar para abrir que se habra for changge state
          // this.$emit('epc-open-split', emitData)
        }
      },

      _HandleShowOperationalStatuses(e) {
        this.$notify.error('cannot_select_in_incident_mode');

        // // Ejemplo: emitir un evento al componente padre
        // this.$emit('epc-doubleclick', {
        //   pic: receivedObj,
        //   picId: receivedObj.picId,
        //   picName: receivedObj.picName,
        //   // ... otros datos que necesites
        // });
      },

      _HandleShowInfoPic(e) {
        const detail = e.detail;

        // Obtener el canvas de PixiJS
        const canvas = this.konvaViewer.app.canvas;
        if (!canvas) {
          console.error('Canvas no encontrado');
          return;
        }

        // Obtener el contenedor del canvas (canvasContainer)
        const container = this.$refs.canvasContainer;
        if (!container) {
          console.error('Container no encontrado');
          return;
        }

        const canvasRect = canvas.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Las coordenadas detail.x y detail.y están en el espacio del stage de PixiJS
        // Necesitamos convertirlas a coordenadas relativas al canvasContainer
        // 1. Convertir coordenadas del stage a coordenadas del canvas
        const canvasX = detail.x;
        const canvasY = detail.y;

        // 2. Convertir coordenadas del canvas a coordenadas relativas al contenedor
        // Restamos la posición del canvas relativa al contenedor
        const relativeX = canvasX + (canvasRect.left - containerRect.left);
        const relativeY = canvasY + (canvasRect.top - containerRect.top);
        // Usar $set para asegurar reactividad
        this.$set(this.popoverPosition, 'x', relativeX);
        this.$set(this.popoverPosition, 'y', relativeY);

        this.popoverData = detail.pic;
        if (this.popoverData.attrs) {
          this.popoverData.attrs.pictogramId = this.pictogramId;
        } else {
          this.popoverData.attrs = { pictogramId: this.pictogramId };
        }
      },

      GetModifiedEntities() {
        return this.modifiedEntities
      },

      // Actualiza las estadísticas de culling desde PixiViewer
      _updateCullingStats() {
        if (this.konvaViewer) {
          this.cullingStats = this.konvaViewer.getCullingStats();
        }
      },

      // Activa/desactiva el indicador de culling
      toggleCullingStats(show) {
        this.showCullingStats = show;
      },

      // ==================== Google Maps Integration ====================

      /**
       * Carga la geo-referencia desde localStorage
       */
      loadGeoReference() {
        this.geoTransform = null;
        this.geoReferenceConfigured = false;

        if (this.konvaViewer) {
          this.konvaViewer.setTransparentBackground(false);
        }

        if (this.pictogramDesign && this.pictogramDesign.metadata && this.pictogramDesign.georeferencing && this.pictogramDesign.georeferencing.active) {

          if (this.pictogramDesign.georeferencing.method === 'center-point') {

            const bounds = this.pictogramDesign.metadata.bounds;

            const centerLocal = {
              x: Math.round(((bounds.minX + bounds.maxX) / 2) * 100) / 100,
              y: Math.round(((bounds.minY + bounds.maxY) / 2) * 100) / 100
            };

            const centerGPS = {
              lat: this.pictogramDesign.georeferencing.settings.centerPointMethod.location.coordinates[1],
              lng: this.pictogramDesign.georeferencing.settings.centerPointMethod.location.coordinates[0]
            };

            this.geoTransform = new GeoTransform(centerLocal, centerGPS);
            // this.geoTransform.saveToLocalStorage(this.pictogramId);
            this.geoReferenceConfigured = true;
          }
        }
      },

      /**
       * Carga el SDK de Google Maps usando el loader compartido del proyecto.
       * Reusable: el loader carga el script una sola vez aunque se invoque desde
       * múltiples componentes. Cuando la promesa resuelve, `window.google.maps`
       * está disponible y marcamos `googleMapsLoaded = true` (data reactivo) para
       * que el resto del flow (computed isGoogleMapsAvailable + watcher) actúe.
       */
      async _checkGoogleMapsAvailability() {
        try {
          await importGoogleMapsLibraries('maps')
          this.googleMapsLoaded = true
        } catch (err) {
          // El loader falló (API key inválida, conexión bloqueada, dominio no
          // autorizado, etc.). Dejamos el flag en false para que el template
          // muestre el mensaje "no cargada" en lugar de intentar inicializar.
          console.error('Google Maps load failed:', err)
          this.googleMapsLoaded = false
        }
      },

      /**
       * Toggle para mostrar/ocultar Google Maps.
       * Si el SDK ya cargó, inicializa inmediato; si no, vuelve a disparar el
       * loader (idempotente) y el watcher de `googleMapsLoaded` se encarga
       * cuando termine.
       */
      toggleGoogleMaps() {
        this.showGoogleMaps = !this.showGoogleMaps;

        if (this.showGoogleMaps) {
          this.konvaViewer.setTransparentBackground(true);
          if (!this.googleMap) {
            if (this.googleMapsLoaded) {
              this.$nextTick(() => this.initGoogleMaps());
            } else {
              this._checkGoogleMapsAvailability()
            }
          }
        } else {
          this.konvaViewer.setTransparentBackground(false);
        }
      },

      /**
       * Inicializa Google Maps
       */
      initGoogleMaps() {
        if (!window.google || !window.google.maps) {
          console.warn('Google Maps API no está cargada');
          return;
        }

        if (!this.geoTransform || !this.dataPictogram) {
          console.warn('Geo-referencia o datos del pictograma no disponibles');
          return;
        }

        // Importante: el método GetScene() del DataPictogram devuelve null DESPUÉS
        // de que ReleaseScene() se ejecuta (optimización de memoria — libera el
        // JSON completo cuando ya extrajo los datos a propiedades top-level).
        // Por eso accedemos directo a dataPictogram.bounds en lugar de
        // GetScene().bounds. Mantenemos el retry para el caso donde el Load
        // aún no terminó de poblar las propiedades.
        const bounds = this.dataPictogram.bounds;
        if (!bounds) {
          this._initGoogleMapsAttempts = (this._initGoogleMapsAttempts || 0) + 1
          if (this._initGoogleMapsAttempts > 100) {
            // ~20s sin bounds → abandonar. Probablemente el load del scene falló.
            console.warn('Bounds del pictograma nunca llegaron — abortando init de Google Maps')
            this._initGoogleMapsAttempts = 0
            return
          }
          setTimeout(() => {
            // Reintentamos solo si el toggle sigue activo y no se inicializó
            // mientras tanto por otro camino.
            if (this.showGoogleMaps && !this.googleMap) {
              this.initGoogleMaps()
            }
          }, 200)
          return
        }
        // bounds OK — resetear el contador de intentos para futuras invocaciones
        this._initGoogleMapsAttempts = 0
        const center = this.geoTransform.getCenterGPS(bounds);
        const zoom = this.geoTransform.getMapZoom(
          bounds,
          this.$refs.canvasContainer.offsetWidth,
          this.$refs.canvasContainer.offsetHeight
        );

        this.googleMap = new google.maps.Map(this.$refs.googleMapsLayer, {
          center: center,
          zoom: zoom,
          mapTypeId: 'satellite',
          disableDefaultUI: true,
          gestureHandling: 'none', // Control desde PixiJS
        });

        // Sincronizar al mover/zoom en PixiJS (implementado via evento __epc_viewport_changed)
      },

      /**
       * Sincroniza el mapa con el viewport de PixiJS
       */
      syncMapWithPixi() {
        // Solo sincronizar si el mapa está visible y configurado
        if (!this.showGoogleMaps || !this.googleMap || !this.geoTransform || !this.konvaViewer) return;

        const viewBounds = this.konvaViewer.getViewportBounds();
        if (!viewBounds) return;

        const center = this.geoTransform.localToGPS(
          (viewBounds.minX + viewBounds.maxX) / 2,
          (viewBounds.minY + viewBounds.maxY) / 2
        );

        const zoom = this.geoTransform.getMapZoom(
          viewBounds,
          this.$refs.canvasContainer.offsetWidth,
          this.$refs.canvasContainer.offsetHeight
        );

        this.googleMap.setCenter(center);
        this.googleMap.setZoom(zoom);
      },
    },

    created() {

      this.uppy = new Uppy({
        debug: process.env.NODE_ENV === 'development', // activamos debug para development
        autoProceed: false,
        allowMultipleUploads: false,
      })
      .use(AwsS3, {
        shouldUseMultipart: true,
        limit: 3,
        companionUrl: '/api/v2/',
        endpoint: '/api/v2/',
        headers: {
          'Authorization': "Bearer " + store.getters["auth/token"]
        }
      })
      // .on('upload-success', (file, response) => { this.onUploadSuccess(file, response) } )
      .on('upload-success', (file, response) => { // callback deveulto cuando el file ha sido subido correctamente
        console.log('%cEPC-TACKER: '+ '%c file ' + file.data.name + ' subido correctamente', 'background: #5577BB; color: #fff', 'color: #000')
      })
      .on('upload-progress', (file, progesss) => { // callback devuelto con el progreso de subido del archivo
        this.uploadPercentage = parseInt(
          Math.round((progesss.bytesUploaded * 100) / progesss.bytesTotal)
        );
      })
      .on('upload-error', (file, error, response) => { // callback devuelto si ha ocurrido algun error en la subida
        console.error('%cEPC-TACKER: '+ '%c a ocurrido un error al subir el archivo ' + file.data.name, 'background: #5577BB; color: #fff', 'color: #000', error)
      })

      document.addEventListener('__epc_pic_select', this._HandleClickPic);
      document.addEventListener('__epc_pic_incident_view_mode_active', this._HandleShowOperationalStatuses);
      document.addEventListener('__epc_pic_show_info', this._HandleShowInfoPic);

      // Listener para sincronizar Google Maps con cambios de viewport en PixiJS
      this._boundSyncMapWithPixi = this.syncMapWithPixi.bind(this);
      document.addEventListener('__epc_viewport_changed', this._boundSyncMapWithPixi);
    },

    async mounted() {

      this.dataPictogram = new DataPictogram();

      this.konvaViewer = new PixiViewer(this.$refs.canvasContainer, this.$refs.refCanvasPixi)

      this.popupXDistanceMin = this.$refs.ref_tooltip.$el.clientWidth
      this.popupYDistanceMin = this.$refs.ref_tooltip.$el.clientHeight

      this.popoverXDistanceMin = this.$refs.ref_popover.$el.clientWidth
      this.popoverYDistanceMin = this.$refs.ref_popover.$el.clientHeight

      // Observa el evento de redimensionamiento de la ventana
      await this.Load()

      if (this.pictogramType === 'mechanical_installation')
        await this._PrepareObjects()
      else
        await this._PrepareObjectsEvacuationLine()

      // Cargar geo-referencia para Google Maps integration
      this.loadGeoReference()
      // Liberar JSON completo de memoria (~1-2MB), ya se extrajeron las referencias necesarias
      this.dataPictogram.ReleaseScene()

      // Ahora sí ocultamos el loader después de que todo está listo
      this.$emit('epc-isloading', false)
      this.isLoading = false
      this.progressText = null
      this.progress = null
      this.curProgressPhase = null

      this.$emit('epc-mounted')

      // Arrancar polling para detectar cuándo Google Maps termina de cargar.
      // El script de Google Maps puede cargar después del mount del componente,
      // y window.google.maps NO es observable por Vue — necesitamos polling.
      this._checkGoogleMapsAvailability()

      // Iniciar actualización de estadísticas de culling
      if (this.showCullingStats) {
        this.cullingStatsInterval = setInterval(() => {
          this._updateCullingStats();
        }, 100); // Actualizar cada 100ms
      }
    },

    destroyed() {
      // Limpiar intervalo de estadísticas de culling
      if (this.cullingStatsInterval) {
        clearInterval(this.cullingStatsInterval);
        this.cullingStatsInterval = null;
      }

      // Destruir Uppy para liberar recursos
      if (this.uppy) {
        this.uppy.destroy();
        this.uppy = null;
      }

      if (this.konvaViewer) {
        this.konvaViewer.Destroy()
      }
      this.konvaViewer = null
      this.dataPictogram = null

      // Cleanup Google Maps
      this.googleMap = null
      this.geoTransform = null
    },

    beforeDestroy() {
      // Remover listeners globales registrados por el componente
      document.removeEventListener('__epc_pic_select', this._HandleClickPic);
      document.removeEventListener('__epc_pic_incident_view_mode_active', this._HandleShowOperationalStatuses); // Corregido: faltaba '__epc_pic_'
      document.removeEventListener('__epc_pic_show_info', this._HandleShowInfoPic);

      // Remover listener de sincronización con Google Maps
      if (this._boundSyncMapWithPixi) {
        document.removeEventListener('__epc_viewport_changed', this._boundSyncMapWithPixi);
      }

      // Limpiar referencias a datos grandes
      this.pics = [];
      this.filterPics = [];
      this.arrTexts = [];
      this.arrGroups = [];
      this.arrStrucComplete = [];
      this.modifiedEntities = [];
      this.selectedObjects = [];

      // Limpiar dataPictogram
      if (this.dataPictogram) {
        this.dataPictogram.Clear();
      }
    }
}
</script>

<style scoped>

.canvasContainer {
    position: relative;
    width: 100%;
    height: 100vh;
    min-width: 100px;
    min-height: 100px;

    background-color: #fefefe;
}

.culling-stats {
    position: absolute;
    top: 10px;
    left: 10px;
    background-color: rgba(0, 0, 0, 0.7);
    color: #00ff00;
    padding: 5px 10px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 12px;
    z-index: 1000;
    pointer-events: none;
}

.selected-stats {
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: rgba(12, 94, 149, 0.9);
    color: #ffffff;
    padding: 8px 14px;
    border-radius: 6px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 13px;
    font-weight: 500;
    z-index: 1000;
    pointer-events: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 6px;
}

.selected-stats .selected-icon {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
}

/* Google Maps Integration Styles */
.google-maps-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0; /* DEBAJO del canvas */
}

/* Cuando el mapa está activo, el contenedor debe ser transparente */
.canvasContainer.map-active {
    background-color: transparent !important;
}

/* El canvas debe estar encima del mapa */
#viewer-pixi.transparent-mode {
    position: absolute !important;
    top: 0;
    left: 0;
    z-index: 1;
    background: transparent !important;
}

#viewer-pixi.transparent-mode canvas {
    background: transparent !important;
}

.google-maps-unavailable {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #666;
    text-align: center;
    padding: 20px;
}

.google-maps-unavailable i {
    font-size: 48px;
    color: #f39c12;
    margin-bottom: 16px;
}

.google-maps-unavailable p {
    font-size: 18px;
    font-weight: 500;
    margin-bottom: 8px;
}

.google-maps-unavailable small {
    color: #999;
}

#viewer-pixi {
    position: relative;
    z-index: 1;
}

/* Control de opacidad del mapa */
.map-opacity-control {
    position: absolute;
    bottom: 55px;
    right: 10px;
    z-index: 100;
    background: rgba(255, 255, 255, 0.95);
    padding: 10px 14px;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 180px;
}

.map-opacity-control label {
    font-size: 12px;
    font-weight: 500;
    color: #333;
    display: flex;
    align-items: center;
    gap: 6px;
}

.map-opacity-control input[type="range"] {
    width: 100%;
    height: 6px;
    cursor: pointer;
    -webkit-appearance: none;
    background: #ddd;
    border-radius: 3px;
}

.map-opacity-control input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    background: #4285f4;
    border-radius: 50%;
    cursor: pointer;
}

.geo-config-modal {
    max-width: 700px;
    width: 100%;
}

.geo-config-modal .modal-card-body {
    min-height: 200px;
}

.geo-config-modal .title.is-6 {
    color: #363636;
    font-weight: 600;
}

.geo-config-modal .label.is-small {
    font-size: 0.75rem;
    margin-bottom: 0.25rem;
}

</style>
